import React from "react";
import { Box, Chip, TextField, Typography } from "@mui/material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { AlertTemplate, AlertVariable } from "../types";

// values are kept as formatted strings ("01-10-2026", "10:00 AM"); parsing them back needs this plugin
dayjs.extend(customParseFormat);

export const DATE_FORMAT = "DD-MM-YYYY";
export const TIME_FORMAT = "hh:mm A";

type Props = {
  template: AlertTemplate;
  /** User-variable values by key (dates as DD-MM-YYYY, times as hh:mm A). */
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  disabled?: boolean;
};

// "…closed on {date} on account of {reason}…" -> ["…closed on ", "{date}", " on account of ", "{reason}", "…"]
const splitBody = (body: string) => body.split(/(\{\w+\})/g).filter((part) => part.length > 0);

/** Inline, fill-in-the-blank field for one user variable. */
const VariableInput: React.FC<{ variable: AlertVariable; value: string; onChange: (value: string) => void; disabled?: boolean }> = ({
  variable,
  value,
  onChange,
  disabled,
}) => {
  const inline = { mx: 0.5, verticalAlign: "baseline" };

  if (variable.kind === "date") {
    return (
      <DatePicker
        format={DATE_FORMAT}
        value={value ? dayjs(value, DATE_FORMAT) : null}
        onChange={(next) => onChange(next && next.isValid() ? next.format(DATE_FORMAT) : "")}
        disabled={disabled}
        slotProps={{ textField: { variant: "standard", size: "small", placeholder: variable.label, sx: { ...inline, width: 140 } } }}
      />
    );
  }
  if (variable.kind === "time") {
    return (
      <TimePicker
        format={TIME_FORMAT}
        value={value ? dayjs(value, TIME_FORMAT) : null}
        onChange={(next) => onChange(next && next.isValid() ? next.format(TIME_FORMAT) : "")}
        disabled={disabled}
        slotProps={{ textField: { variant: "standard", size: "small", placeholder: variable.label, sx: { ...inline, width: 120 } } }}
      />
    );
  }
  const max = variable.maxLength;
  return (
    <TextField
      variant="standard"
      size="small"
      placeholder={variable.label}
      value={value}
      onChange={(e) => onChange(max ? e.target.value.slice(0, max) : e.target.value)}
      disabled={disabled}
      helperText={max ? `${value.length}/${max}` : undefined}
      slotProps={{ formHelperText: { sx: { textAlign: "right", m: 0, fontSize: 11 } } }}
      sx={{ ...inline, width: Math.min(Math.max((max ?? 20) * 7, 120), 220) }}
    />
  );
};

/**
 * The template body as a normal paragraph: text as-is, user variables as inline inputs, system
 * variables (filled by the backend per recipient) as grey placeholders.
 */
const TemplateParagraph: React.FC<Props> = ({ template, values, onChange, disabled }) => {
  const byKey = Object.fromEntries(template.variables.map((v) => [v.key, v]));

  return (
    <Typography component="div" sx={{ lineHeight: 2.6, fontSize: 15, color: "text.primary" }}>
      {splitBody(template.body).map((part, index) => {
        const match = part.match(/^\{(\w+)\}$/);
        const variable = match ? byKey[match[1]] : undefined;
        if (!variable) return <React.Fragment key={index}>{part}</React.Fragment>;
        if (variable.source === "system") {
          return (
            <Chip
              key={index}
              label={variable.label}
              size="small"
              variant="outlined"
              sx={{ mx: 0.5, color: "text.secondary", bgcolor: "grey.100", borderStyle: "dashed", fontStyle: "italic" }}
            />
          );
        }
        return (
          <Box key={index} component="span" sx={{ display: "inline-flex" }}>
            <VariableInput variable={variable} value={values[variable.key] ?? ""} onChange={(value) => onChange(variable.key, value)} disabled={disabled} />
          </Box>
        );
      })}
    </Typography>
  );
};

export default TemplateParagraph;
